import Highcharts from 'highcharts';
import sortBy from 'lodash/sortBy';
import {AttrsSchema, Component} from 'panel';
import HighchartsAnnotations from 'highcharts/modules/annotations';

import {patchHighchartsForNegativeLog} from 'common/report/util/highcharts';
import {abbreviateNumber, htmlEncodeString} from 'common/util';
import {isIncompleteInterval} from 'common/util/chart';
import * as colors from 'common/util/colors';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {DEFAULT_CHART_HEIGHT} from 'common/report/constants';
import mpFonts from 'common/stylesheets/mixins/fonts.json';
import {getMpContext} from 'common/mp-context';

import {DisplayAnalysisType, DisplayPlotStyle, LineStyle, XAxisUnit} from './enums';
import {IChartDataItem, LineChartState, XAxisOptions} from './types';
import template from './index.jade';
import css from './index.styl?inline';
import {Annotations} from './annotations';

patchHighchartsForNegativeLog(Highcharts);
HighchartsAnnotations(Highcharts);

export const LINE_CHART_PROP_KEYS = [
  `chartData`,
  `formatPointX`,
  `formatPointY`,
  `formatTicksX`,
  `formatTooltip`,
  `formatXAxisLabel`,
  `formatYAxisLabel`,
  `onHighChartClick`,
  `onHighchartMouseOut`,
  `onHighchartMouseOver`,
] as const;

type ArrayType<T> = T extends ReadonlyArray<infer U> ? U : never;

export type LineChartProps = Pick<LineChart, ArrayType<typeof LINE_CHART_PROP_KEYS>>;

export interface LineChartAttrs {
  'chart-height': number;
  'chart-width': number;
  'chart-margin-left': number;
  'chart-margin-right': number;
  'color-theme': string;
  'display-analysis-type': DisplayAnalysisType;
  'display-plot-style': DisplayPlotStyle;
  'download-mode': boolean;
  'incomplete-to-date': boolean;
  'incomplete-from-date': boolean;
  'hide-y-axis-ticks': boolean;
  'reflow-interval': number;
  'show-annotations': boolean;
  'spacing-bottom': number;
  'time-unit': TimeUnit;
  'trend-unit': TimeUnit;
  'x-axis-options': XAxisOptions;
  'y-axis-label-alignment': string;
  'y-axis-label-horizontal-offset': number;
  'y-axis-label-vertical-offset': number;
  'zoom-type': string;
}

export class LineChart extends Component<LineChartState, LineChartAttrs> {
  public formatPointX?: (x: number) => string;
  public formatPointY?: (y: number) => string;
  public formatTicksX?: (min: number, max: number) => number[];
  public formatYAxisLabel?: (value: number) => string;
  public formatXAxisLabel?: (value: number) => string;
  public onHighchartMouseOut?: (highchartChart: any) => void;
  public onHighchartMouseOver?: (
    highchartChart: any,
    seriesIdx: number,
    dataIdx: number,
    color: string,
    plotX: number,
    plotY: number,
  ) => void;
  public onHighChartClick?: (
    highchartChart: any,
    seriesIdx: number,
    dataIdx: number,
    color: string,
    plotX: number,
    plotY: number,
  ) => void;
  public formatTooltip?: (x: number, y: number, series: any, idx: number) => string;
  annotations: Annotations;

  public static get attrsSchema(): AttrsSchema<LineChartAttrs> {
    return {
      ...super.attrsSchema,
      'chart-height': {type: `number`, default: DEFAULT_CHART_HEIGHT, description: `height of the chart in px`},
      'chart-width': {type: `number`, default: 800, description: `width of the chart in px`},
      'chart-margin-left': {
        type: `number`,
        default: 50,
        description: `left margin of the chart in px`,
      },
      'chart-margin-right': {
        type: `number`,
        default: 10,
        description: `right margin of the chart in px`,
      },
      'color-theme': {
        type: `string`,
        default: `default`,
        description: `color theme of the lines plotted on the chart`,
      },
      'display-analysis-type': {
        type: `string`,
        default: DisplayAnalysisType.Linear,
        enum: [DisplayAnalysisType.Linear, DisplayAnalysisType.Logarithmic],
      },
      'display-plot-style': {
        type: `string`,
        default: DisplayPlotStyle.Standard,
        enum: [DisplayPlotStyle.Stacked, DisplayPlotStyle.Standard],
        description: `plot style, standard vs stacked`,
      },
      'download-mode': `boolean`,
      'incomplete-to-date': {
        type: `boolean`,
        description: `zone with to date, overrides incomplete interval check, utc offset from project, which is only necessary if you want incomplete interval zoning`,
      },
      'incomplete-from-date': {
        type: `boolean`,
        description: `zone with from date as well, utc offset from project, which is only necessary if you want incomplete interval zoning`,
      },
      'hide-y-axis-ticks': {
        type: `boolean`,
        description: `hide y axis values to the right of the chart`,
      },
      'reflow-interval': {
        type: `number`,
        description: `sets an interval for how often the chart is redrawn and updated`,
      },
      'show-annotations': {
        type: `boolean`,
        description: `shows global annotations on mp-line-chart`,
      },
      'spacing-bottom': {
        type: `number`,
        description: `sets an space between the bottom edge of the chart and the content`,
        default: 15,
      },
      'time-unit': {
        type: `string`,
        default: null,
        enum: [TimeUnit.Day, TimeUnit.Month, TimeUnit.Week],
        description: `unit to align, which is only necessary if you want incomplete interval zoning`,
      },
      'trend-unit': {
        type: `string`,
        default: TimeUnit,
        enum: [TimeUnit.Minute, TimeUnit.Hour, TimeUnit.Day, TimeUnit.Month, TimeUnit.Week, TimeUnit.Quarter],
        description: `unit of line-chart that will be used as key for annotations map`,
      },
      'x-axis-options': {
        type: `json`,
        description: `x-axis overrides for highcharts of type XAxisOptions`,
      },
      'y-axis-label-alignment': {
        type: `string`,
        description: `adds an alignment for the y axis labels, such as left or right aligned`,
      },
      'y-axis-label-horizontal-offset': {
        type: `number`,
        description: `adds a horizontal position offset to the y axis labels`,
      },
      'y-axis-label-vertical-offset': {
        type: `number`,
        description: `adds a vertical position offset to the y axis labels`,
      },
      'zoom-type': {
        type: `string`,
        default: ``,
        enum: [``, `x`],
      },
    };
  }

  public get chartData(): IChartDataItem[] {
    return this._chartData;
  }

  public set chartData(value: IChartDataItem[]) {
    this._chartData = value;
    this.updateChart();
  }

  private chart: any;
  private highcharts: any;
  private reflowInterval: NodeJS.Timeout;
  private _chartData: IChartDataItem[];
  private hoveredTimestampMs: any;

  public get config() {
    return {
      css,
      useShadowDom: true,
      template,
      helpers: {
        getChartData: () => this.chartData,
        getChartElement: () => this.el,
        getHighCharts: () => this.highcharts,
      },
    };
  }

  public connectedCallback() {
    super.connectedCallback();

    if (!this.chartData) {
      throw new Error(`"chartData" is a required component prop`);
    }

    const chartEl = document.createElement(`div`);
    this.el.appendChild(chartEl);
    this.highcharts = new Highcharts.Chart(chartEl, this.getChartOptions());
    this.updateChart();

    if (this.attr(`show-annotations`)) {
      this.annotations = new Annotations(this);
      this.update({annotations: this.annotations.getDefaultState()});

      this.setConfig(`helpers`, {
        ...this.getConfig(`helpers`),
        annotations: this.annotations.getHelpers(),
      });

      this.annotations.generateTickMarks();
      this.annotations.load();
    }
  }

  public disconnectedCallback() {
    if (this.highcharts) {
      this.highcharts.destroy();
      delete this.highcharts;
    }

    if (this.chart) {
      this.chart.remove();
      delete this.chart;
    }

    super.disconnectedCallback();
  }

  getChartData() {
    return this.chartData;
  }

  getHighcharts() {
    return this.highcharts;
  }

  getHoveredTimestampMs() {
    return this.hoveredTimestampMs;
  }

  reflowChart() {
    window.requestAnimationFrame(() => {
      // another check to prevent premature disconnection from throwing error
      if (this.highcharts) {
        this.highcharts.reflow();
      }
    });
  }

  private updateChart() {
    if (this.attr(`reflow-interval`)) {
      clearInterval(this.reflowInterval);
      this.reflowInterval = setInterval(() => this.reflowChart(), this.attr(`reflow-interval`));
    }
    if (this.highcharts) {
      this.highcharts.update(
        this.getChartOptions(),
        true, // redraw after update
        true, // oneToOne: add/remove series when they change
      );
      // expand chart to full area width even if chart is fully loaded but we don't know the width size
      this.reflowChart();
    }
  }

  private formatSeries() {
    // prettier-ignore
    return this.chartData
      .map((dataSegment, segmentIdx) => {
        const colorIdx = segmentIdx % this.chartData.length;
        return {
          name: dataSegment.name,
          label: dataSegment.label,
          data: dataSegment.data,
          type: this.attr(`display-plot-style`) === DisplayPlotStyle.Stacked ? `area` : `line`,
          color: dataSegment.color || colors.getChartSegmentColorCombo(colorIdx, this.attr(`color-theme`)).primary,
          visible: dataSegment.visible,
          dashStyle: dataSegment.dashStyle || LineStyle.Standard,
          incompleteLength: dataSegment.incompleteLength,
        };
      })
      // TODO opslead
      // eslint-disable-next-line sonarjs/cognitive-complexity
      .map((lineSeries) => {
        lineSeries.data = sortBy(lineSeries.data, (d) => d[0]);
        const timeUnit = this.attr(`time-unit`);
        let incompletePathsAndZoning = {};
        const dataLength = lineSeries.data.length;
        const showIncompleteToDate = this.attr(`incomplete-to-date`);
        const showIncompleteFromDate = this.attr(`incomplete-from-date`);
        const dashStyle = lineSeries.dashStyle;
        const incompleteLength =
          lineSeries.incompleteLength === undefined || lineSeries.incompleteLength > dataLength - 1
            ? 1
            : lineSeries.incompleteLength;
        if (timeUnit) {
          const utcOffset = getMpContext().utcOffset;
          const isIncompletePath = isIncompleteInterval(lineSeries.data, {
            unit: timeUnit,
            utcOffset,
          });
          const showIncomplete = isIncompletePath && lineSeries.data.length >= 2;
          incompletePathsAndZoning = {
            isIncompletePath,
            ...(showIncomplete && {
              zoneAxis: `x`,
              zones: [{value: lineSeries.data[dataLength - 2][0]}, {dashStyle: LineStyle.Incomplete}],
            }),
          };
        } else if (dataLength > 1 && incompleteLength > 0 && (showIncompleteFromDate || showIncompleteToDate)) {
          const zones = [];
          if (showIncompleteFromDate && showIncompleteToDate) {
            // Highchart has a bug with [dot, solid, dot]. So when we are changing the line style more then once,
            // we need to make sure we define both the starting and ending position for the solid part of the line.
            zones.push({value: lineSeries.data[incompleteLength][0], dashStyle: LineStyle.Incomplete});
            if (incompleteLength + 1 < dataLength - incompleteLength - 1) {
              zones.push({value: lineSeries.data[incompleteLength + 1][0], dashStyle});
              zones.push({value: lineSeries.data[dataLength - incompleteLength - 1][0], dashStyle});
            } else if (incompleteLength + 1 === dataLength - incompleteLength - 1) {
              zones.push({value: lineSeries.data[incompleteLength + 1][0], dashStyle});
            }
            zones.push({value: lineSeries.data[dataLength - 1][0], dashStyle: LineStyle.Incomplete});
          } else if (showIncompleteFromDate) {
            zones.push({value: lineSeries.data[incompleteLength][0], dashStyle: LineStyle.Incomplete});
            if (incompleteLength < dataLength - 1) {
              zones.push({value: lineSeries.data[incompleteLength + 1][0], dashStyle});
            }
          } else {
            // Only showIncompleteToDate is true
            if (incompleteLength < dataLength - 1) {
              zones.push({value: lineSeries.data[dataLength - incompleteLength - 1][0], dashStyle});
            }
            zones.push({value: lineSeries.data[dataLength - 1][0], dashStyle: LineStyle.Incomplete});
          }
          incompletePathsAndZoning = {
            isIncompletePath: true,
            zoneAxis: `x`,
            zones,
          };
        }
        return {
          ...lineSeries,
          ...incompletePathsAndZoning,
          formatPointX: this.formatPointX,
          formatPointY: this.formatPointY,
          formatTicksX: this.formatTicksX,
          onHighchartMouseOut: this.onHighchartMouseOut,
          onHighchartMouseOver: this.onHighchartMouseOver,
          onHighChartClick: this.onHighChartClick,
          formatTooltip: this.formatTooltip,
        };
      });
  }

  // TODO: Highcharts 7 is typed in beta, but we are using Highcharts 6. Use Highcharts 7's typing system
  // when we eventually switch over
  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  private getChartOptions(): any {
    const highchartChart = this.highcharts;
    const series = this.formatSeries();
    const xAxisOptions: XAxisOptions = this.attr(`x-axis-options`);
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const mpLineChartComponent = this;

    return {
      chart: {
        backgroundColor: null,
        height: `${this.attr(`chart-height`)}px`,
        marginLeft: this.attr(`chart-margin-left`),
        marginRight: this.attr(`chart-margin-right`),
        style: {
          fontFamily: mpFonts.fontFamilyPrimary,
          fontSize: `12px,`,
        },
        spacingBottom: this.attr(`spacing-bottom`),
        type: this.attr(`display-plot-style`) === DisplayPlotStyle.Stacked ? `area` : `line`,
        width: this.attr(`chart-width`),
        zoomType: this.attr(`zoom-type`),
        events: {
          redraw(this: TODO) {
            if (mpLineChartComponent.annotations) {
              mpLineChartComponent.annotations.setAnnotations(
                this.xAxis[0].tickPositions[0],
                this.xAxis[0].tickPositions[this.xAxis[0].tickPositions.length - 1],
              );
            }
          },
        },
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      loading: {
        labelStyle: {
          color: `transparent`,
        },
      },
      plotOptions: {
        line: {
          lineWidth: 2,
          marker: {
            symbol: `circle`,
            lineColor: `#fff`,
            lineWidth: 2,
            radius: 5,
          },
          shadow: false,
          states: {
            hover: {
              enabled: true,
            },
          },
        },
        series: {
          animation: false,
          fillOpacity: this.attr(`display-plot-style`) === DisplayPlotStyle.Stacked ? 0.2 : 1,
          marker: {
            enabled: false,
            hover: {
              enabled: true,
            },
            lineWidth: 2,
            symbol: `circle`,
          },
          point: {
            events: {
              mouseOver(this: TODO) {
                mpLineChartComponent.hoveredTimestampMs = this.x;
                if (mpLineChartComponent.annotations) {
                  mpLineChartComponent.annotations.redrawCreateButton();
                }
                const {color, index: dataIdx, plotX, plotY, series: chartSeries} = this;
                const {onHighchartMouseOver, index: seriesIdx} = chartSeries.userOptions;
                return onHighchartMouseOver
                  ? onHighchartMouseOver(highchartChart, seriesIdx, dataIdx, color, plotX, plotY)
                  : undefined;
              },
              click(this: TODO) {
                const {color, index: dataIdx, plotX, plotY, series: chartSeries} = this;
                const {onHighChartClick} = chartSeries.userOptions;
                return onHighChartClick ? onHighChartClick(chartSeries, dataIdx, color, plotX, plotY) : undefined;
              },
            },
          },
          events: {
            mouseOut(this: TODO) {
              const {onHighchartMouseOut} = this.userOptions;
              return onHighchartMouseOut ? onHighchartMouseOut(highchartChart) : undefined;
            },
          },
          shadow: false,
          ...(this.attr(`display-plot-style`) === DisplayPlotStyle.Stacked && {
            stacking: `normal`,
          }),
        },
      },
      series,
      time: {
        useUTC: false,
      },
      tooltip: {
        backgroundColor: null,
        borderWidth: 0,
        formatter(this: TODO) {
          // Highcharts callbacks don't work with arrow functions
          const {color, index, name, options} = this.series;

          // Override style to make the tooltip not look like the default
          if (options.formatTooltip) {
            return options.formatTooltip(this.x, this.y, this.series, index);
          }

          const formattedLeftLabel = options.formatPointX
            ? options.formatPointX(this.x)
            : Highcharts.dateFormat(`%b %e, %Y`, this.x);
          const formattedValue = options.formatPointY ? options.formatPointY(this.y) : this.y.toLocaleString();
          const seriesLabel = name;

          const defaultLabelFormatter = `
            <div class="row">
              <span class="value" style="font-weight: 700">${htmlEncodeString(formattedLeftLabel)}:</span>
              <span class="value">${formattedValue}</span>
            </div>
          `;
          return `
            <div class="line-chart-tooltip">
              <div class="title" style="background-color: ${color};">${seriesLabel}</div>
              <div class="result">
                ${defaultLabelFormatter}
              </div>
            </div>
          `;
        },
        hideDelay: 0,
        padding: 0,
        shadow: false,
        shape: `square`,
        useHTML: true,
      },
      title: {
        text: null,
      },
      xAxis: {
        endOnTick: false,
        formatTicksX: this.formatTicksX,
        ...(this.formatXAxisLabel ? {formatXAxisLabel: this.formatXAxisLabel} : {}),
        labels: {
          enabled: !xAxisOptions?.labels?.hideTicks,
          staggerLines: xAxisOptions?.labels?.staggerLines,
          step: xAxisOptions?.labels?.step,
          ...(xAxisOptions?.labels?.style ? {style: xAxisOptions?.labels?.style} : {}),
          ...(this.formatXAxisLabel
            ? {
                formatter(this: TODO) {
                  const {userOptions} = this.axis;
                  return userOptions.formatXAxisLabel(this.value);
                },
              }
            : {}),
        },
        crosshair: xAxisOptions?.crosshair,
        lineColor: colors.mpColors[`$gray2`],
        lineWidth: 1,
        marginBottom: 10,
        maxPadding: 0.02,
        minPadding: 0.02,
        plotBands: xAxisOptions?.plotBands,
        plotLines: xAxisOptions?.plotLines,
        tickPosition: `outside`,
        tickPositions: xAxisOptions?.tickPositions,
        tickPositioner(this: TODO) {
          const {dataMax, dataMin, options} = this;
          const {formatTicksX} = options;
          return formatTicksX ? formatTicksX(dataMin, dataMax) : undefined;
        },
        tickmarkPlacement: `on`,
        title: {
          text: null,
        },
        type: xAxisOptions?.unit === XAxisUnit.Number ? `linear` : `datetime`,
      },
      yAxis: {
        allowDecimals: true,
        endOnTick: false,
        gridLineColor: colors.mpColors[`$gray2`],
        gridLineDashStyle: `shortDash`,
        formatYAxisLabel: this.formatYAxisLabel,
        labels: {
          enabled: !this.attr(`hide-y-axis-ticks`),
          ...(this.attr(`y-axis-label-alignment`) ? {align: this.attr(`y-axis-label-alignment`)} : {}),
          ...(this.attr(`y-axis-label-horizontal-offset`) ? {x: this.attr(`y-axis-label-horizontal-offset`)} : {}),
          ...(this.attr(`y-axis-label-vertical-offset`) ? {y: this.attr(`y-axis-label-vertical-offset`)} : {}),
          formatter(this: TODO) {
            const {userOptions} = this.axis;
            return userOptions.formatYAxisLabel
              ? userOptions.formatYAxisLabel(this.value)
              : abbreviateNumber(this.value);
          },
        },
        lineWidth: 1,
        startOnTick: true,
        title: {
          text: null,
        },
        type:
          this.attr(`display-analysis-type`) === DisplayAnalysisType.Logarithmic
            ? DisplayAnalysisType.Logarithmic
            : DisplayAnalysisType.Linear,
      },
    };
  }
}

export const LineChartEl = defineAndMakeJsxFactory(`mp-line-chart`, LineChart);
